import { Popover, Spin } from "antd";
import Check from "common/icons/CheckGreen.svg";
import FilterIcon from "common/icons/FilterGreen.svg";
import RadioCheck from "common/icons/RadioCheckGreen.svg";
import RadioUncheck from "common/icons/RadioUncheckGreen.svg";
import Uncheck from "common/icons/UncheckGreen.svg";
import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

export const selectStyle = {
  width: "200px",
  height: "36px",
  padding: "12px",
  fontSize: "10px"
};

const FiltersContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  height: 56px;
  width: 176px;
  flex-shrink: 0;
  border-radius: 4px;
  border: 1px solid rgba(0, 87, 84, 0.1);
  background: linear-gradient(180deg, #fff 0%, #f5f5f5 100%);
  box-shadow: 0px 4px 6px -3px rgba(154, 188, 187, 0.6);
  cursor: pointer;

  @media screen and (max-width: 1279px) {
    height: 38px;
    width: 120px;
    padding: 12px;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 0;
  margin: 0px;
  column-gap: 10px;
  color: #005754;
  font-family: Poppins;
  font-size: 15px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media screen and (max-width: 1280px) {
    font-size: 12px;
    column-gap: 8px;
  }
`;

const MenuWrapper = styled.div`
  margin: 0;
  min-width: 220px;
  display: flex;
  flex-direction: column;
  row-gap: 6px;
`;

const MenuTitle = styled.div`
  color: #005754;
  font-family: Poppins;
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 8px;
`;

const MenuContainer = styled.div`
  display: flex;
  align-items: center;
  column-gap: 8px;
  border-radius: 4px;
  border: 1px solid rgba(0, 87, 84, 0.1);
  background: linear-gradient(180deg, #fff 0%, #f5f5f5 100%);
  padding: 6px;
  cursor: pointer;
  user-select: none;

  &:hover {
    background: rgba(0, 87, 84, 0.05);
  }
`;

const OptionsWrapper = styled.div`
  max-height: 240px; /* ≈ 6 items */
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  row-gap: 5px;

  /* Optional smooth scroll */
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 87, 84, 0.3);
    border-radius: 4px;
  }
`;

const LabelContainer = styled.div`
  color: #2e2e2e;
  font-family: Poppins;
  font-size: 12px;
  text-transform: uppercase;
`;

const CustomPopover = styled(Popover)`
  .ant-popover-inner {
    padding: 4px; /* reduce padding around the options */
    background: #fff;
    border-radius: 4px;
  }

  .ant-popover-inner-content {
    padding: 0; /* remove inner content padding */
  }
`;

const Img = styled.img`
  width: 24px;
  height: 24px;

  @media screen and (max-width: 1279px) {
    width: 16px;
    height: 16px;
  }
`;

const CheckImg = styled.img`
  width: 18px;
  height: 18px;

  @media screen and (max-width: 1279px) {
    width: 12px;
    height: 12px;
  }
`;

const CustomSelect = (props) => {
  const {
    menuTitle = "",
    isLoading = false,
    filterData = [],
    selectedOption = [],
    setSelectedOption = () => {},
    setPagination = () => {},
    pagination = { pageSize: 10, currentPage: 0 },
    defaultStatuses = [],
    isMultiSelect = false,
    containerStyle = {},
    type = "",
    showAll = true
  } = props;

  const [open, setOpen] = useState(false);
  const [statusLabel, setStatusLabel] = useState("All");

  const statusIds = useMemo(
    () => filterData.map((item) => item.id),
    [filterData]
  );

  const statusMap = useMemo(() => {
    const map = {};
    filterData.forEach((item) => {
      map[item.id] = item.label;
    });
    return map;
  }, [filterData]);

  useEffect(() => {
    if (!isMultiSelect) {
      if (selectedOption && statusMap[selectedOption]) {
        setStatusLabel(statusMap[selectedOption]);
      } else {
        setStatusLabel(`All ${type}`);
      }
      return;
    }

    if (selectedOption.length === statusIds.length) {
      setStatusLabel(`All ${type}`);
    } else if (selectedOption.length === 1) {
      setStatusLabel(statusMap[selectedOption[0]]);
    } else {
      setStatusLabel(`Selected (${selectedOption.length})`);
    }
  }, [selectedOption, statusIds.length, statusMap, isMultiSelect, type]);

  const resetPagination = () => {
    setPagination({
      currentPage: 0,
      pageSize: pagination.pageSize
    });
  };

  const handleMultiSelect = (item) => {
    resetPagination();

    if (item.id === 0) {
      if (selectedOption.length === statusIds.length) {
        if (statusIds.length > 1) {
          setSelectedOption([statusIds[0]]);
        } else {
          setSelectedOption([]);
        }
      } else {
        setSelectedOption(statusIds);
      }
      return;
    }
    let updated = [...selectedOption];
    const index = updated.indexOf(item.id);

    if (index === -1) {
      updated.push(item.id);
    } else {
      updated.splice(index, 1);
    }

    if (updated.length === 0) {
      updated = defaultStatuses;
    }
    if (updated.length === 0 && statusIds.length > 0) {
      updated = [statusIds[0]];
    }

    setSelectedOption(updated);
  };

  const handleSingleSelect = (item) => {
    resetPagination();
    // Toggle: if the same item is clicked, unselect it
    if (selectedOption === item.id) {
      setSelectedOption(null);
    } else {
      setSelectedOption(item.id);
    }
    setOpen(false);
  };

  const content = (
    <MenuWrapper>
      <MenuTitle>{menuTitle}</MenuTitle>

      {/* ALL OPTION */}
      {showAll && (
        <MenuContainer
          onClick={() =>
            isMultiSelect
              ? handleMultiSelect({ id: 0 })
              : handleSingleSelect({ id: 0 })
          }
        >
          {isMultiSelect ? (
            <CheckImg
              src={selectedOption.length === statusIds.length ? Check : Uncheck}
            />
          ) : (
            <Img src={!selectedOption ? RadioCheck : RadioUncheck} />
          )}
          <LabelContainer>All</LabelContainer>
        </MenuContainer>
      )}

      {/* OPTIONS */}
      <OptionsWrapper>
        {filterData.map((item) => (
          <MenuContainer
            key={item.id}
            onClick={() =>
              isMultiSelect ? handleMultiSelect(item) : handleSingleSelect(item)
            }
          >
            {isMultiSelect ? (
              <CheckImg
                src={selectedOption.includes(item.id) ? Check : Uncheck}
              />
            ) : (
              <Img
                src={selectedOption === item.id ? RadioCheck : RadioUncheck}
              />
            )}
            <LabelContainer>{item.label}</LabelContainer>
          </MenuContainer>
        ))}
      </OptionsWrapper>
    </MenuWrapper>
  );

  return (
    <CustomPopover
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomLeft"
      content={content}
      arrow={false}
    >
      <FiltersContainer style={containerStyle}>
        <ContentContainer>
          {isLoading ? <Spin /> : <Img src={FilterIcon} />}
          {statusLabel}
        </ContentContainer>
        <span>▼</span>
      </FiltersContainer>
    </CustomPopover>
  );
};

export default CustomSelect;
